import { useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toaster } from '@optiaxiom/react';
import { useQueryClient } from '@tanstack/react-query';

import { getDeepLinkPath, getNotificationIntent } from '@frontend/features/notifications/utils';

import { useInstanceId } from './useInstanceId';
import { useNamespacedNavigate } from './useNamespacedNavigate';
import { useSocketChannel } from './useSocket';

type NotificationCreatedData = {
  notification_id: string;
  opal_instance_id: string;
  type: string;
  title: string;
  message: string;
  agent_id?: string;
  metadata?: {
    execution_id?: string;
    thread_id?: string;
    [key: string]: unknown;
  };
  created_at: string;
};

export function useNotificationEvents() {
  const { instanceId } = useInstanceId();
  const location = useLocation();
  const navigate = useNamespacedNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const onNotificationCreated = useCallback(
    (data: unknown) => {
      const notification = data as NotificationCreatedData;

      // Ignore notifications for other instances
      if (notification.opal_instance_id !== instanceId) {
        return;
      }

      queryClient.setQueryData<number>(['notifications', 'unread-count'], currentCount =>
        currentCount == null ? currentCount : currentCount + 1
      );
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });

      // Suppress toast if user is viewing the relevant thread
      if (notification.metadata?.thread_id) {
        const currentThreadId = searchParams.get('threadId');
        if (currentThreadId === notification.metadata.thread_id) {
          return;
        }
      }

      // Suppress toast if user is viewing the same conversation in the messaging tab.
      // Read URL directly because chat-frontend sets params via replaceState, which
      // doesn't trigger React Router's useSearchParams re-render.
      if (notification.type?.startsWith('messaging_')) {
        const currentParams = new URLSearchParams(window.location.search);
        const viewingConversationId = currentParams.get('conversationId');
        const notifConversationId = (notification.metadata as Record<string, unknown>)
          ?.conversation_id as string | undefined;
        if (
          currentParams.get('tab') === 'messages' &&
          viewingConversationId &&
          viewingConversationId === notifConversationId
        ) {
          return;
        }
      }

      // Suppress toast if user is viewing the relevant execution
      if (notification.metadata?.execution_id) {
        const isOnExecutionPage = location.pathname.includes(
          `/executions/${notification.metadata.execution_id}`
        );
        if (isOnExecutionPage) {
          return;
        }
      }

      toaster.create(notification.title, {
        action: 'View',
        intent: getNotificationIntent(notification.type),
        onAction: () => {
          if (notification.type?.startsWith('messaging_')) {
            const meta = notification.metadata as Record<string, unknown> | undefined;
            document.dispatchEvent(
              new CustomEvent('opal::im::notificationClicked', {
                detail: {
                  conversationId: meta?.conversation_id,
                  messageId: meta?.message_id,
                  msgThreadId: meta?.thread_id,
                },
                bubbles: true,
              })
            );
          }
          navigate(getDeepLinkPath(notification));
        },
      });
    },
    [instanceId, location.pathname, navigate, queryClient, searchParams]
  );

  useSocketChannel('notification_created', onNotificationCreated);
}
